// --- Dependencies -------------------------------------------------------------------------------
import { useEffect, useState } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import useFetch from "@/hooks/useFetch";
import useLang from "@/hooks/useLang";
import usePageSize from "@/hooks/usePageSize";
import useTranslation from "@/hooks/useTranslation";
import type { TyRecordUpdate } from "@/types";
import { formatDateTime, readLangValue } from "@/utils";
import Icon from "@/UI/atoms/Icon";
import EmptyState from "@/UI/components/EmptyState";
import type { TyIconName } from "@/UI/atoms/Icon/types";
import Paginator from "@/UI/components/Paginator";
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
export type TyHistoryDetails = {
    title: string;
    /** The record's own history route — `/properties/units/:id/updates`. */
    path: string;
};

type Props = { onClose: () => void; details?: unknown };

/** What each kind of entry looks like, so a history can be skimmed down the icons alone. */
const icons: Record<string, TyIconName> = {
    created: "add",
    updated: "edit",
    statusChanged: "refresh",
    assigned: "team",
    cancelled: "close",
    deleted: "trash",
    restored: "refresh",
    cleaned: "clear",
};

/**
 * Turns a stored change into something readable.
 *
 * `changes` holds raw values on purpose — the log stays searchable and renders in either language —
 * so the field name and its values are translated here, at the moment they are shown. Only `status`
 * has a vocabulary today; anything else is printed as stored rather than guessed at, which is the
 * honest fallback when a field arrives that nobody has taught this screen about yet.
 */
function readChange(change: { field: string; from: unknown; to: unknown }, t: ReturnType<typeof useTranslation>) {
    const statuses = t.properties.statuses as Record<string, string>;
    const value = (raw: unknown) => {
        if (raw === null || raw === undefined || raw === "") return t.properties.notSet;
        return change.field === "status" ? (statuses[String(raw)] ?? String(raw)) : String(raw);
    };

    const labels: Record<string, string> = { status: t.properties.fields.status };
    return { label: labels[change.field] ?? change.field, from: value(change.from), to: value(change.to) };
}

/**
 * One record's history, off its own header.
 *
 * A record's past belongs beside the record, not in a log somewhere else that has to be searched by
 * name — by the time you want to know who changed a price, you are already looking at the price.
 *
 * Entries are read on open rather than carried by the record: `updates` is `select: false`, so a list
 * of projects never drags twenty histories along for a screen showing none of them.
 *
 * The sentences were composed in both languages **when the thing happened** and are shown as written.
 * Re-rendering them from today's translations would quietly rewrite what somebody read last year.
 */
export default function HistoryPopup({ onClose, details }: Props) {
    const t = useTranslation();
    const lang = useLang((state) => state.lang) ?? "ar";
    const { title, path } = details as TyHistoryDetails;

    const [page, setPage] = useState(1);
    const pageSize = usePageSize((state) => state.pageSize);
    const { data, loading, error, invoke } = useFetch<{ updates: TyRecordUpdate[]; total: number }>(path, {}, false);

    useEffect(() => {
        invoke({ query: { limit: pageSize, page } });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, pageSize]);
    const updates = data?.updates ?? [];

    return (
        <section className="history-popup">
            <header className="history-popup__head">
                <div className="history-popup__identity">
                    <h2 className="history-popup__title">{t.history.title}</h2>
                    <span className="history-popup__subject">{title}</span>
                </div>
                <button type="button" className="history-popup__close" onClick={onClose} aria-label={t.common.close}>
                    <Icon name="close" size={16} />
                </button>
            </header>

            <div className="history-popup__body">
                {loading && <p className="history-popup__state">{t.common.loading}</p>}
                {error && <p className="history-popup__state history-popup__state--error">{error}</p>}
                {!loading && !error && !updates.length && <EmptyState icon="history" title={t.history.empty} />}

                <ol className="history-popup__list">
                    {updates.map((entry, index) => (
                        <li key={`${entry.at}-${index}`} className="history-popup__entry">
                            <span className={`history-popup__mark history-popup__mark--${entry.action}`}>
                                <Icon name={icons[entry.action] ?? "edit"} size={14} />
                            </span>

                            <div className="history-popup__content">
                                <p className="history-popup__message">{readLangValue(entry.message, lang)}</p>

                                {/* The detail behind the sentence: what moved, from what, to what. */}
                                {entry.changes.length > 0 && (
                                    <ul className="history-popup__changes">
                                        {entry.changes.map((change) => {
                                            const read = readChange(change, t);
                                            return (
                                                <li key={change.field}>
                                                    <span className="history-popup__field">{read.label}</span>
                                                    <span className="history-popup__from">{read.from}</span>
                                                    <Icon name="chevron" size={11} />
                                                    <span className="history-popup__to">{read.to}</span>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                )}

                                <span className="history-popup__meta">
                                    {entry.actor.name ?? t.history.system} · {formatDateTime(entry.at, lang)}
                                </span>
                            </div>
                        </li>
                    ))}
                </ol>

                <Paginator page={page} total={data?.total ?? 0} count={updates.length} onPage={setPage} />
            </div>
        </section>
    );
}
